import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Brain, MessageSquare, Check, X, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { format } from 'date-fns';

export default function BotMemoryReviewDialog({ open, onOpenChange, feedbackEntry, botProfile }) {
  const queryClient = useQueryClient();
  const [ownerInstructions, setOwnerInstructions] = React.useState('');
  const [selectedRetain, setSelectedRetain] = React.useState([]);
  const [selectedRemove, setSelectedRemove] = React.useState([]);

  // Parse learnings from feedback content
  const parseLearnings = (content) => {
    if (!content) return [];
    const lines = content.split('\n');
    const learnings = [];
    let inList = false;
    
    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed.startsWith('- ') || trimmed.startsWith('* ') || trimmed.match(/^\d+\./)) {
        learnings.push(trimmed.replace(/^[-*\d.]\s*/, ''));
        inList = true;
      } else if (inList && trimmed && !trimmed.startsWith('#') && !trimmed.startsWith('**')) {
        // Continue capturing if it's a continuation
        if (learnings.length > 0) {
          learnings[learnings.length - 1] += ' ' + trimmed;
        }
      } else if (trimmed.startsWith('#') || trimmed.startsWith('**')) {
        inList = false;
      }
    }
    return learnings.filter(l => l.length > 10); // Filter out very short items
  };

  const learnings = React.useMemo(() => 
    parseLearnings(feedbackEntry?.content), 
    [feedbackEntry?.content]
  );

  const updateMemoryMutation = useMutation({
    mutationFn: async (data) => {
      const response = await base44.functions.invoke('updateBotMemoryFromFeedback', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['botMemory'] });
      queryClient.invalidateQueries({ queryKey: ['pendingFeedback'] });
      onOpenChange(false);
      setOwnerInstructions('');
      setSelectedRetain([]);
      setSelectedRemove([]);
    }
  });

  const handleRetainToggle = (learning) => {
    setSelectedRetain(prev => 
      prev.includes(learning) 
        ? prev.filter(l => l !== learning)
        : [...prev, learning]
    );
    // Remove from "remove" list if added to retain
    setSelectedRemove(prev => prev.filter(l => l !== learning));
  };

  const handleRemoveToggle = (learning) => {
    setSelectedRemove(prev => 
      prev.includes(learning) 
        ? prev.filter(l => l !== learning)
        : [...prev, learning]
    );
    // Remove from "retain" list if added to remove
    setSelectedRetain(prev => prev.filter(l => l !== learning));
  };

  const handleSubmit = () => {
    updateMemoryMutation.mutate({
      feedback_memory_id: feedbackEntry.id,
      bot_profile_id: botProfile.id,
      owner_instructions: ownerInstructions,
      items_to_retain: selectedRetain,
      items_to_remove: selectedRemove
    });
  };

  const handleApproveAll = () => {
    updateMemoryMutation.mutate({
      feedback_memory_id: feedbackEntry.id,
      bot_profile_id: botProfile.id,
      owner_instructions: 'Approved all learnings.',
      items_to_retain: learnings,
      items_to_remove: []
    });
  };

  if (!feedbackEntry) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="profile-botmemoryreviewdialog-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-pink-400" />
            Review Bot Learnings
            <Badge variant="outline" className="ml-2 border-amber-500/30 text-amber-400">
              {format(new Date(feedbackEntry.date), 'MMM d, yyyy')}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-6">
            {/* Bot's Message */}
            <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <MessageSquare className="w-4 h-4 text-purple-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-purple-400 mb-2">
                    {botProfile?.callsign || 'Bot'} says:
                  </p>
                  <div className="prose prose-sm prose-invert max-w-none">
                    <ReactMarkdown>{feedbackEntry.content}</ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>

            {/* Learnings Selection */}
            {learnings.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">
                  Select what to retain or remove:
                </h4>
                <div className="space-y-2">
                  {learnings.map((learning, index) => (
                    <div 
                      key={index} 
                      className={`p-3 rounded-lg border transition-colors ${
                        selectedRetain.includes(learning) 
                          ? 'bg-emerald-500/10 border-emerald-500/30' 
                          : selectedRemove.includes(learning)
                          ? 'bg-rose-500/10 border-rose-500/30'
                          : 'bg-slate-800/30 border-slate-700'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex flex-col gap-2 pt-1">
                          <div className="flex items-center gap-2">
                            <Checkbox 
                              id={`retain-${index}`}
                              checked={selectedRetain.includes(learning)}
                              onCheckedChange={() => handleRetainToggle(learning)}
                              className="border-emerald-500 data-[state=checked]:bg-emerald-500"
                            />
                            <Label 
                              htmlFor={`retain-${index}`} 
                              className="text-xs text-emerald-400 cursor-pointer"
                            >
                              Retain
                            </Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox 
                              id={`remove-${index}`}
                              checked={selectedRemove.includes(learning)}
                              onCheckedChange={() => handleRemoveToggle(learning)}
                              className="border-rose-500 data-[state=checked]:bg-rose-500"
                            />
                            <Label 
                              htmlFor={`remove-${index}`} 
                              className="text-xs text-rose-400 cursor-pointer"
                            >
                              Remove
                            </Label>
                          </div>
                        </div>
                        <p className="text-sm text-slate-300 flex-1">{learning}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Additional Instructions */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-300">
                Additional instructions for the bot (optional):
              </Label>
              <Textarea data-testid="profile-botmemoryreviewdialog-textarea"
                value={ownerInstructions}
                onChange={(e) => setOwnerInstructions(e.target.value)}
                placeholder="E.g., 'Also remember that we prefer X over Y' or 'Focus more on tracking patterns in category Z'"
                className="bg-slate-800 border-slate-700 text-slate-100 min-h-[100px]"
              />
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="flex gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-slate-700"
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={handleApproveAll}
            disabled={updateMemoryMutation.isPending}
            className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
          >
            <Check className="w-4 h-4 mr-2" />
            Approve All
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={updateMemoryMutation.isPending}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {updateMemoryMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <Brain className="w-4 h-4 mr-2" />
                Update Memory
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}